/* Kindly "Find my size" estimator: pure calculator (window.KindlySizeFinder / module.exports, tested in
   v2/test/sizefinder.test.js) + the DOM wiring for snippets/kindly-size-finder.liquid.
   Runs fully in the browser: no network calls, nothing stored (no cookies, no localStorage).

   How the size is picked
   1. A measured calf beats everything: it is mapped straight to the size table.
   2. No calf: the calf is ESTIMATED from height and weight.
      calf_cm ~= 18 + 0.7 x BMI   (BMI = kg / m^2), clamped to 28-52 cm.
      Basis: in adult population anthropometry (NHANES-style body measurement surveys) calf circumference rises
      roughly 0.7 cm per BMI unit, and sits around 33-34 cm at a BMI of 22 and ~39 cm at a BMI of 30. It is an
      estimate for picking a starting size, not a measurement; the result card says so and links to the measuring steps.
      The BMI value is never shown to the visitor.
   3. Overlaps (14-15, 17-18, 22-23 in) go to the LARGER size ("between sizes? go roomy"). An estimated calf within
      1 in of a size boundary (the middle of an overlap: 14.5, 17.5, 22.5 in) also goes to the larger size, because
      the estimate is uncertain. A measured calf only goes up inside an overlap, so it is never put in a size whose calf
      range starts above it. An estimate tops out at 52 cm (20.5 in), so it never reaches 3XL Wide: at the ceiling the card
      tells the visitor to measure, because 3XL Wide may fit better.
   4. Shoe size is a cross-check only: if it disagrees, the card keeps the calf result and adds a short note.
   Size table: snippets/kindly-size-table.liquid / DEFAULTS.md row 20. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.KindlySizeFinder = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var CM_PER_IN = 2.54, KG_PER_LB = 0.45359237;
  var SIZES = [
    { name: 'S/M', calf: [11, 15], women: [5, 8], men: [4, 6.5] },
    { name: 'L/XL', calf: [14, 18], women: [8.5, 12], men: [7, 11] },
    { name: '2XL Wide', calf: [17, 23], women: [9, 13], men: [9, 13] },
    { name: '3XL Wide', calf: [22, 30], women: [9, 13], men: [9, 13] }
  ];
  /* EU shoe sizes are unisex: EU -> US women's ~ EU - 31, US men's ~ EU - 33. A size "fits" an EU shoe if either does. */
  var EU_TO_W = 31, EU_TO_M = 33;
  var LIMITS = {
    heightIn: [48, 86], heightCm: [122, 218],
    weightLb: [70, 550], weightKg: [32, 250],
    calfIn: [8, 40], calfCm: [20, 100],
    women: [4, 15], men: [3, 16], eu: [33, 50],
    bmi: [12, 70]
  };
  var ESTIMATE_CLAMP_CM = [28, 52];

  function num(v) {
    if (v == null) return null;
    var s = String(v).trim().replace(',', '.');
    if (s === '') return null;
    if (!/^\d*\.?\d+$/.test(s)) return NaN;
    return parseFloat(s);
  }
  function inRange(v, r) { return v >= r[0] && v <= r[1]; }
  function round1(v) { return Math.round(v * 10) / 10; }
  function clamp(v, r) { return Math.min(r[1], Math.max(r[0], v)); }
  function fmt(v) { return String(round1(v)); }

  function heightToCm(ft, inch) { return ((ft || 0) * 12 + (inch || 0)) * CM_PER_IN; }
  function lbToKg(lb) { return lb * KG_PER_LB; }
  function inToCm(i) { return i * CM_PER_IN; }
  function cmToIn(c) { return c / CM_PER_IN; }

  function estimateCalfCm(heightCm, weightKg) {
    var m = heightCm / 100;
    var bmi = weightKg / (m * m);
    return clamp(18 + 0.7 * bmi, ESTIMATE_CLAMP_CM);
  }

  /* calfIn -> { index, size, between, outside: 'below'|'above'|null } */
  function sizeForCalf(calfIn, estimated) {
    var last = SIZES.length - 1;
    if (calfIn < SIZES[0].calf[0]) return { index: 0, size: SIZES[0].name, between: false, outside: 'below' };
    if (calfIn > SIZES[last].calf[1]) return { index: last, size: SIZES[last].name, between: false, outside: 'above' };
    var idx = 0;
    for (var i = 0; i <= last; i++) if (inRange(calfIn, SIZES[i].calf)) idx = i; /* largest size that contains it */
    var between = idx > 0 && inRange(calfIn, SIZES[idx - 1].calf); /* sits in an overlap, took the larger one */
    if (idx < last) {
      var next = SIZES[idx + 1];
      var boundary = (next.calf[0] + SIZES[idx].calf[1]) / 2;
      if (calfIn >= boundary - 1 && (estimated || calfIn >= next.calf[0])) { idx += 1; between = true; }
    }
    return { index: idx, size: SIZES[idx].name, between: between, outside: null };
  }

  /* shoe = { system: 'women'|'men'|'eu', value } -> size names whose shoe range includes it */
  function sizesForShoe(shoe) {
    return SIZES.filter(function (s) {
      if (shoe.system === 'eu') return inRange(shoe.value - EU_TO_W, s.women) || inRange(shoe.value - EU_TO_M, s.men);
      return inRange(shoe.value, shoe.system === 'men' ? s.men : s.women);
    }).map(function (s) { return s.name; });
  }

  function joinOr(list) {
    if (list.length <= 1) return list.join('');
    return list.slice(0, -1).join(', ') + ' or ' + list[list.length - 1];
  }

  /* input (strings or numbers, as typed):
     { units: 'us'|'metric', ft, inch, lb, cm, kg, shoeSystem: 'women'|'men' (US only), shoe, calf }
     -> { ok:false, errors:{field:msg}, message } or { ok:true, size, ... card text } */
  function recommend(input) {
    var metric = input.units === 'metric';
    var errors = {};
    var calfRaw = num(input.calf), shoeRaw = num(input.shoe);
    var calfIn = null;
    if (calfRaw !== null) {
      var cr = metric ? LIMITS.calfCm : LIMITS.calfIn;
      if (isNaN(calfRaw)) errors.calf = 'Please enter your calf as a number, like ' + (metric ? '38' : '15') + '.';
      else if (!inRange(calfRaw, cr)) errors.calf = 'Calf should be between ' + cr[0] + ' and ' + cr[1] + (metric ? ' cm' : ' inches') + '. Please check the number.';
      else calfIn = metric ? cmToIn(calfRaw) : calfRaw;
    }

    var heightCm = null, weightKg = null;
    if (calfIn === null) {
      if (metric) {
        var cm = num(input.cm);
        if (cm === null) errors.height = 'Please enter your height in cm.';
        else if (isNaN(cm) || !inRange(cm, LIMITS.heightCm)) errors.height = 'Height should be between ' + LIMITS.heightCm[0] + ' and ' + LIMITS.heightCm[1] + ' cm.';
        else heightCm = cm;
      } else {
        var ft = num(input.ft), inch = num(input.inch);
        if (ft === null && inch === null) errors.height = 'Please enter your height, like 5 ft 4 in.';
        else if (isNaN(ft) || isNaN(inch) || (inch !== null && inch >= 12) || (ft !== null && ft % 1 !== 0)) errors.height = 'Please enter feet and inches, like 5 ft 4 in.';
        else {
          var totalIn = (ft || 0) * 12 + (inch || 0);
          if (!inRange(totalIn, LIMITS.heightIn)) errors.height = 'Height should be between 4 ft and 7 ft 2 in.';
          else heightCm = inToCm(totalIn);
        }
      }
      var w = num(metric ? input.kg : input.lb);
      var wr = metric ? LIMITS.weightKg : LIMITS.weightLb;
      if (w === null) errors.weight = 'Please enter your weight in ' + (metric ? 'kg.' : 'pounds.');
      else if (isNaN(w) || !inRange(w, wr)) errors.weight = 'Weight should be between ' + wr[0] + ' and ' + wr[1] + (metric ? ' kg.' : ' lb.');
      else weightKg = metric ? w : lbToKg(w);
    }

    var shoe = null;
    if (shoeRaw !== null) {
      var system = metric ? 'eu' : (input.shoeSystem === 'men' ? 'men' : 'women');
      var sr = LIMITS[system];
      if (isNaN(shoeRaw) || !inRange(shoeRaw, sr) || (shoeRaw * 2) % 1 !== 0) {
        errors.shoe = 'Shoe size should be between ' + sr[0] + ' and ' + sr[1] + (metric ? ' (EU).' : ' (US, whole or half sizes).');
      } else shoe = { system: system, value: shoeRaw };
    }

    if (calfIn === null && heightCm !== null && weightKg !== null) {
      var m = heightCm / 100, bmi = weightKg / (m * m);
      if (!inRange(bmi, LIMITS.bmi)) errors.weight = 'Those numbers look unusual. Please double-check your height and weight.';
    }

    var keys = Object.keys(errors);
    if (keys.length) {
      var message = (errors.calf && calfRaw !== null && keys.length === 1) ? errors.calf : 'Please check the highlighted ' + (keys.length === 1 ? 'box.' : 'boxes.');
      return { ok: false, errors: errors, message: message };
    }

    var measured = calfIn !== null;
    var estCm = measured ? null : estimateCalfCm(heightCm, weightKg);
    if (!measured) calfIn = cmToIn(estCm);
    var pick = sizeForCalf(calfIn, !measured);
    var res = {
      ok: true, size: pick.size, measured: measured, between: pick.between, outside: pick.outside,
      calfIn: round1(calfIn), calfCm: round1(inToCm(calfIn)),
      headline: 'We recommend ' + pick.size
    };

    if (measured) {
      res.why = 'Based on your calf measurement of ' + (metric ? fmt(calfRaw) + ' cm.' : fmt(calfRaw) + ' inches.');
      res.confidence = 'Measured calf: the most reliable way to pick a size.';
    } else {
      if (metric) {
        var c = Math.round(inToCm(calfIn));
        res.calfText = 'about ' + (c - 1) + '–' + (c + 1) + ' cm';
      } else {
        var lo = Math.floor(calfIn);
        res.calfText = 'about ' + lo + '–' + (lo + 1) + ' inches';
      }
      res.why = 'Based on your height and weight, your calf is probably ' + res.calfText + '.';
      res.confidence = 'This is an estimate. Measuring your calf takes a minute and makes it exact.';
    }

    if (pick.outside === 'below') res.rangeNote = 'Your calf is a little slimmer than our smallest size. S/M is the closest fit.';
    else if (pick.outside === 'above') res.rangeNote = 'Your calf is larger than our biggest size (' + (metric ? '76 cm' : '30 in') + '). 3XL Wide is the closest. Email us at hello@kindlysocks.com and we will help you choose.';
    if (!measured && estCm >= ESTIMATE_CLAMP_CM[1]) res.rangeNote = 'Your calf may be wider than we can estimate. 3XL Wide could fit better, so please measure your calf to be sure.';
    else if (!measured && estCm <= ESTIMATE_CLAMP_CM[0]) res.rangeNote = 'Your calf may be slimmer than we can estimate. Please measure your calf to be sure.';
    if (res.between) res.betweenNote = 'Between sizes? Go roomy. You are close to the line, so we picked the roomier size for comfort.';

    if (shoe) {
      var fits = sizesForShoe(shoe);
      res.shoeFits = fits;
      if (!fits.length) res.shoeNote = 'Your shoe size is outside our chart. Your calf decides the compression fit.';
      else if (fits.indexOf(pick.size) === -1) res.shoeNote = 'Your shoe size usually fits ' + joinOr(fits) + '. Your calf decides the compression fit.';
      else res.shoeNote = 'Your shoe size fits ' + pick.size + ' too.';
      res.shoeAgrees = fits.indexOf(pick.size) !== -1;
    }
    res.exchange = 'Not quite right? Your first size exchange is free.';
    return res;
  }

  /* Unit toggle helpers: convert what is already typed so nobody has to retype. */
  function convertFields(v, toMetric) {
    var out = {}, n;
    if (toMetric) {
      var ft = num(v.ft), inch = num(v.inch);
      if ((ft || inch) && !isNaN(ft) && !isNaN(inch)) out.cm = String(Math.round(heightToCm(ft, inch)));
      n = num(v.lb); if (n && !isNaN(n)) out.kg = String(Math.round(lbToKg(n)));
      n = num(v.calf); if (n && !isNaN(n)) out.calf = String(Math.round(inToCm(n)));
      n = num(v.shoe); if (n && !isNaN(n)) out.shoe = String(Math.round(n + (v.shoeSystem === 'men' ? EU_TO_M : EU_TO_W)));
    } else {
      n = num(v.cm);
      if (n && !isNaN(n)) { var ti = Math.round(cmToIn(n)); out.ft = String(Math.floor(ti / 12)); out.inch = String(ti % 12); }
      n = num(v.kg); if (n && !isNaN(n)) out.lb = String(Math.round(n / KG_PER_LB));
      n = num(v.calf); if (n && !isNaN(n)) out.calf = String(round1(cmToIn(n))); /* 1 decimal: rounding must not flip the size */
      n = num(v.shoe); if (n && !isNaN(n)) out.shoe = String(Math.round((n - (v.shoeSystem === 'men' ? EU_TO_M : EU_TO_W)) * 2) / 2);
    }
    return out;
  }

  /* ---------- DOM ---------- */
  function initOne(box, api) {
    var form = box.querySelector('[data-ksf-form]');
    var card = box.querySelector('[data-ksf-result]');
    var summary = box.querySelector('[data-ksf-summary]');
    var FIELDS = ['ft', 'inch', 'lb', 'cm', 'kg', 'shoe', 'calf'];
    function field(n) { return box.querySelector('[data-ksf-in="' + n + '"]'); }
    function units() { var r = box.querySelector('[data-ksf-units]:checked'); return r ? r.value : 'us'; }
    function shoeSystem() { var r = box.querySelector('[data-ksf-shoe-sys]:checked'); return r ? r.value : 'women'; }
    function values() {
      var v = { units: units(), shoeSystem: shoeSystem() };
      FIELDS.forEach(function (n) { var f = field(n); v[n] = f ? f.value : ''; });
      return v;
    }
    function applyUnits() {
      var metric = units() === 'metric';
      box.classList.toggle('is-metric', metric);
      box.querySelectorAll('[data-ksf-us]').forEach(function (e) { e.hidden = metric; });
      box.querySelectorAll('[data-ksf-metric]').forEach(function (e) { e.hidden = !metric; });
    }
    function clearErrors() {
      box.querySelectorAll('[data-ksf-err]').forEach(function (e) { e.textContent = ''; e.hidden = true; });
      box.querySelectorAll('.ksf__input').forEach(function (i) { i.removeAttribute('aria-invalid'); });
      summary.textContent = ''; summary.hidden = true;
    }
    var lastUnits = units();
    box.querySelectorAll('[data-ksf-units]').forEach(function (r) {
      r.addEventListener('change', function () {
        var now = units();
        if (now === lastUnits) return;
        var conv = api.convertFields(values(), now === 'metric');
        (now === 'metric' ? ['cm', 'kg'] : ['ft', 'inch', 'lb']).forEach(function (n) { field(n).value = conv[n] || ''; });
        ['calf', 'shoe'].forEach(function (n) { if (field(n).value) field(n).value = conv[n] || ''; });
        lastUnits = now;
        applyUnits(); clearErrors();
        if (!card.hidden) submit();
      });
    });

    function line(cls, text) { var p = document.createElement('p'); p.className = cls; p.textContent = text; return p; }
    function submit() {
      clearErrors();
      var r = api.recommend(values());
      if (!r.ok) {
        card.hidden = true;
        var first = null;
        Object.keys(r.errors).forEach(function (k) {
          var e = box.querySelector('[data-ksf-err="' + k + '"]');
          if (e) { e.textContent = r.errors[k]; e.hidden = false; }
          var ins = k === 'height' ? (units() === 'metric' ? ['cm'] : ['ft', 'inch']) : k === 'weight' ? [units() === 'metric' ? 'kg' : 'lb'] : [k];
          ins.forEach(function (n) { var f = field(n); if (f) { f.setAttribute('aria-invalid', 'true'); if (!first) first = f; } });
        });
        summary.textContent = r.message; summary.hidden = false;
        if (first) first.focus();
        return;
      }
      var body = card.querySelector('[data-ksf-body]');
      body.innerHTML = '';
      body.appendChild(line('ksf__eyebrow', r.measured ? 'From your calf measurement' : 'Your estimated size'));
      var h = document.createElement('p'); h.className = 'ksf__size'; h.setAttribute('data-ksf-size', r.size);
      h.appendChild(document.createTextNode('We recommend '));
      var b = document.createElement('b'); b.textContent = r.size; h.appendChild(b);
      body.appendChild(h);
      body.appendChild(line('ksf__why', r.why));
      if (r.rangeNote) body.appendChild(line('ksf__note', r.rangeNote));
      if (r.betweenNote) body.appendChild(line('ksf__note', r.betweenNote));
      if (r.shoeNote) body.appendChild(line('ksf__note' + (r.shoeAgrees ? ' is-ok' : ''), r.shoeNote));
      body.appendChild(line('ksf__conf', r.confidence));
      var use = card.querySelector('[data-ksf-use]');
      if (use) { use.setAttribute('data-size', r.size); use.textContent = 'Use ' + r.size; }
      card.querySelector('[data-ksf-measure]').hidden = r.measured;
      card.hidden = false;
      card.focus({ preventScroll: true });
      if (card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });

    /* PDP only: select the size in the bundle builder (clicks the real size pill, so the builder's own logic runs). */
    var use = card.querySelector('[data-ksf-use]');
    if (use) use.addEventListener('click', function () {
      var size = use.getAttribute('data-size');
      var pdp = box.closest('[data-kindly-pdp]');
      var pill = pdp && Array.prototype.filter.call(pdp.querySelectorAll('.kb__pill'), function (p) { return p.getAttribute('data-size') === size; })[0];
      if (!pill) return;
      pill.click();
      if (pill.getAttribute('aria-checked') !== 'true') { use.textContent = size + ' cannot be picked right now. Please choose a size below.'; return; }
      var dlg = box.closest('dialog');
      if (dlg && dlg.open) dlg.close();
      var sz = pdp.querySelector('.kb__size');
      if (sz && sz.scrollIntoView) sz.scrollIntoView({ behavior: 'smooth', block: 'center' });
      pill.focus({ preventScroll: true });
    });
    applyUnits();
  }

  function initAll() {
    if (typeof document === 'undefined') return;
    var api = (typeof window !== 'undefined' && window.KindlySizeFinder) || API;
    document.querySelectorAll('[data-kindly-sizefinder]').forEach(function (box) {
      if (box.dataset.ksfReady) return;
      box.dataset.ksfReady = '1';
      initOne(box, api);
    });
  }

  var API = {
    SIZES: SIZES, LIMITS: LIMITS,
    heightToCm: heightToCm, lbToKg: lbToKg, inToCm: inToCm, cmToIn: cmToIn,
    estimateCalfCm: estimateCalfCm, sizeForCalf: sizeForCalf, sizesForShoe: sizesForShoe,
    recommend: recommend, convertFields: convertFields, initAll: initAll
  };
  if (typeof document !== 'undefined' && typeof window !== 'undefined' && !(typeof module === 'object' && module.exports)) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
    else initAll();
  }
  return API;
});
